'use client';

import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

/** Idle time before spotlight clears — matches hero journey dot-mark */
export const CURSOR_SPOTLIGHT_IDLE_MS = 300;

type CursorSpotlightOptions = {
  idleMs?: number;
  enabled?: boolean;
  /** When set, pointer must pass this check in addition to the host bounding box. */
  isActiveAt?: (clientX: number, clientY: number, host: HTMLElement) => boolean;
};

/**
 * Tracks pointer position relative to `hostRef` and calls `onSpotlight` with
 * host-local coordinates, or `null` when the pointer leaves or idles out.
 */
export function useCursorSpotlight(
  hostRef: RefObject<HTMLElement | null>,
  onSpotlight: (position: { x: number; y: number } | null) => void,
  options?: CursorSpotlightOptions,
): void {
  const idleMs = options?.idleMs ?? CURSOR_SPOTLIGHT_IDLE_MS;
  const enabled = options?.enabled ?? true;
  const isActiveAt = options?.isActiveAt;
  const onSpotlightRef = useRef(onSpotlight);
  const isActiveAtRef = useRef(isActiveAt);

  useLayoutEffect(() => {
    onSpotlightRef.current = onSpotlight;
    isActiveAtRef.current = isActiveAt;
  }, [isActiveAt, onSpotlight]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const host = hostRef.current;
    if (host === null) {
      return;
    }

    const idleTimer = createRestartableTimeout();

    const onMouseMove = (event: MouseEvent): void => {
      const rect = host.getBoundingClientRect();
      const insideHost =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      const activeAt =
        insideHost &&
        (isActiveAtRef.current === undefined ||
          isActiveAtRef.current(event.clientX, event.clientY, host));

      if (activeAt) {
        onSpotlightRef.current({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
        idleTimer.restart(() => {
          onSpotlightRef.current(null);
        }, idleMs);
      } else {
        idleTimer.cancel();
        onSpotlightRef.current(null);
      }
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return (): void => {
      idleTimer.cancel();
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [enabled, hostRef, idleMs]);
}

interface RestartableTimeout {
  readonly restart: (callback: () => void, delayMs: number) => void;
  readonly cancel: () => void;
}

function createRestartableTimeout(): RestartableTimeout {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancel = (): void => {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  };

  const restart = (callback: () => void, delayMs: number): void => {
    cancel();
    timer = setTimeout(() => {
      timer = undefined;
      callback();
    }, delayMs);
  };

  return { restart, cancel };
}
